import { AccessToken, OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, Security } from "@okta/okta-react";
import config from "Config/config";
import { getCognitoConfig } from "Config/cognitoConfig";
import getAuth from "Services/auth";
import { RCGApolloProvider } from "frontend/src/components/RCGApolloProvider";
import i18next from "i18next";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { store } from "../stores/store";
import { ApolloProviderFallback } from "./ApolloProviderFallback";
import { HistoryLogger } from "./HistoryLogger";
import { RequiredAuth } from "./RequiredAuth";
import MercuryApp from "./mercury_app";
import { CognitoAuthProvider, useCognitoAuth } from "./CognitoAuthProvider";
import { CognitoLoginPage } from "./CognitoLoginPage";
import { CognitoCallback } from "./CognitoCallback";
import { ThemeModeProvider } from "frontend/src/theme/ThemeModeProvider";
import { SnackbarProvider, notify } from "frontend/src/feedback/SnackbarProvider";

interface Services {
  auth: OktaAuth;
  graphqlUrl: string;
  translationLoaded?: boolean;
  webSocketUrl: string;
  useCognito: boolean;
}

// Cognito's getIdToken() already checks expiry and refreshes; on a dead
// session (refresh failed / no refresh token) it returns null, so signal
// the user and force a fresh login instead of sending a dead token.
const CognitoRCGApolloProvider: React.FC<{
  url: string;
  webSocketUrl: string;
  children: JSX.Element | JSX.Element[];
}> = ({ url, webSocketUrl, children }) => {
  const { getIdToken, signOut } = useCognitoAuth();

  const getAccessToken = useCallback(async () => {
    const idToken = await getIdToken();
    if (!idToken) {
      notify({
        severity: "info",
        message: "Session expired, logging out, unable to renew..",
        duration: 30000, // ms (was antd's 30s)
        key: "signout",
      });
      await signOut();
      throw new Error("Session expired");
    }
    return idToken;
  }, [getIdToken, signOut]);

  return (
    <RCGApolloProvider
      url={url}
      getToken={getAccessToken}
      FallbackComponent={ApolloProviderFallback}
      webSocketUrl={webSocketUrl}
    >
      {children}
    </RCGApolloProvider>
  );
};

const AppBase = () => {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri, window.location.origin));
  };

  const [services, setServices] = useState<Services>();

  useEffect(() => {
    const auth = getAuth(config.OKTA_ISSUER(), config.OKTA_CLIENT_ID());

    // Check if Cognito is configured
    const useCognito = !!getCognitoConfig();

    setServices({
      auth,
      graphqlUrl: config.RCG_GRAPHQL_URI(),
      translationLoaded: i18next.hasLoadedNamespace("translations"),
      webSocketUrl: config.RCG_SIRIUS_WS_URI(),
      useCognito,
    });

    // enable okta auto refresh if not using Cognito
    if (!useCognito) {
      auth.start();
    }

    return () => {
      if (!useCognito) {
        auth.stop();
      }
    };
  }, []);

  // Okta token retrieval
  const getAccessToken = useCallback(async () => {
    if (!services) return undefined;

    const { tokenManager } = services.auth;
    const accessToken = (await tokenManager.get("accessToken")) as
      | AccessToken
      | undefined;
    if (!accessToken) throw Error("No access token");
    if (
      tokenManager.hasExpired(accessToken) ||
      tokenManager.getExpireTime(accessToken) <
        (Date.now() + 1000 * 60 * 5) / 1000
    ) {
      try {
        await tokenManager.renew("accessToken");
        const newAccessToken = (await tokenManager.get(
          "accessToken"
        )) as AccessToken;

        if (tokenManager.hasExpired(newAccessToken)) {
          throw Error("Failed to renew token");
        }
        return newAccessToken.accessToken;
      } catch {
        notify({ severity: "info",
          message: "Session expired, logging out, unable to renew..",
          duration: 30,
          key: "signout",
        });
        services.auth.signOut();
      }
    }
    return accessToken.accessToken;
  }, [services?.auth]);

  // we still need to wait for config to be loaded to load the inside here
  if (!services) return null;

  // Cognito auth flow
  if (services.useCognito) {
    const cognitoConfig = getCognitoConfig();
    if (!cognitoConfig) {
      throw new Error("Cognito config missing despite Cognito auth mode");
    }

    return (
      <CognitoAuthProvider config={cognitoConfig}>
        <Routes>
          <Route path="/login" element={<CognitoLoginPage />} />
          <Route path="/cognito/callback" element={<CognitoCallback />} />

          <Route path="*" element={<RequiredAuth useCognito={true} />}>
            <Route
              path="*"
              element={
                <CognitoRCGApolloProvider
                  url={services.graphqlUrl}
                  webSocketUrl={services.webSocketUrl}
                >
                  <ReduxProvider store={store}>
                    <MercuryApp />
                  </ReduxProvider>
                </CognitoRCGApolloProvider>
              }
            />
          </Route>
        </Routes>
      </CognitoAuthProvider>
    );
  }

  // Okta auth flow (legacy)
  return (
    <Security oktaAuth={services.auth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/implicit/callback" element={<LoginCallback />} />

        <Route path="*" element={<RequiredAuth useCognito={false} />}>
          <Route
            path="*"
            element={
              <RCGApolloProvider
                url={services.graphqlUrl}
                getToken={getAccessToken}
                FallbackComponent={ApolloProviderFallback}
                webSocketUrl={services.webSocketUrl}
              >
                <HistoryLogger auth={services.auth} />
                <ReduxProvider store={store}>
                  <MercuryApp />
                </ReduxProvider>
              </RCGApolloProvider>
            }
          />
        </Route>
      </Routes>
    </Security>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <SnackbarProvider>
        <BrowserRouter>
          <AppBase />
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeModeProvider>
  );
}

App.displayName = "App";

export default App;
